import { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, Group, Loader, Stack, Text } from '@mantine/core';
import { IconCheck } from '@tabler/icons-react';
import type { Row } from '@tanstack/react-table';
import { DriveLayout } from './DriveLayout';
import RepositoryTable from '@ayocore/platform/client/components/RepositoryTable/RepositoryTable';
import logger from '@ayocore/platform/common/helpers/logger';
import type { UploadedFile } from '../../../schemas/uploaded_file.schema';
import { useDriveEntryRepository, useUploadedFileRepository } from '../../rpc';
import { FolderTree } from './FolderTree';
import { DriveBreadcrumbs } from './DriveBreadcrumbs';
import { buildFolderPath } from './driveScope';
import { buildDriveColumns } from './driveColumns';
import { useDriveFolders } from './useDriveFolders';
import { driveRowId, isFolderRow, resolveFileId, type DriveRow } from './driveRow';

export interface DriveFilePickerModalProps extends Record<string, unknown> {
  /** true — выбор нескольких файлов (чекбокс-подобно, тоггл), false — одного. */
  multiple?: boolean;
  /**
   * Папка, с которой открыть пикер (по умолчанию — корень Диска). Дерево всё
   * равно показывает весь доступный Диск — это лишь стартовая папка.
   */
  rootFolderId?: number;
  /** Подтверждение выбора — всегда массив (в single-режиме длиной 1). */
  onSelect: (files: UploadedFile[]) => void;
  /** Отмена (кнопка «Отмена»). */
  onClose?: () => void;
}

/**
 * DriveFilePickerModal — выбор файла(ов) с Диска: слева дерево папок, справа
 * таблица содержимого выбранной папки — как на странице Диска, но read-only и в
 * режиме выбора (эталон паттерна выделения — identity/UserPickerModal).
 *
 * - клик по папке (в дереве или таблице) — переход внутрь;
 * - клик по файлу/ярлыку — выделение (single заменяет, multiple тогглит);
 * - двойной клик по файлу в single-режиме сразу подтверждает;
 * - «Выбрать» перечитывает чистые UploadedFile[] по id и отдаёт в onSelect.
 *
 * Ярлык выбирается как его файл-ОРИГИНАЛ (resolveFileId → entry_file_id), поэтому
 * оригинал и ярлык одного файла — одно выделение. Папки не выбираются.
 * Открывается через useModal без обёртки <Modal>.
 *
 * @example
 * ```tsx
 * const picker = useModal(DriveFilePickerModal);
 * picker.open({
 *   title: 'Выбрать файл',
 *   multiple: false,
 *   onSelect: files => { console.log(files); picker.close(); },
 *   onClose: () => picker.close(),
 * }, { size: 'xl' });
 * ```
 */
export function DriveFilePickerModal({
  multiple = false,
  rootFolderId,
  onSelect,
  onClose,
}: DriveFilePickerModalProps) {
  const fileRepo = useUploadedFileRepository();
  const driveRepo = useDriveEntryRepository();

  const {
    folders,
    foldersById,
    rootFolder,
    loading,
    error: loadError,
    reload: loadFolders,
  } = useDriveFolders();

  /** Ошибка панели: либо загрузка Диска, либо неудачный выбор файлов. */
  const [error, setError] = useState<string | null>(null);
  const [activeFolderId, setActiveFolderId] = useState<number | null>(rootFolderId ?? null);
  const [submitting, setSubmitting] = useState(false);
  /** Выбранные строки файлов/ярлыков (идентичность — resolveFileId). */
  const [selected, setSelected] = useState<DriveRow[]>([]);

  // Стартовая папка: rootFolderId из пропа, иначе корень Диска (после загрузки).
  useEffect(() => {
    if (activeFolderId == null && rootFolder) setActiveFolderId(rootFolder.id);
  }, [rootFolder, activeFolderId]);

  const breadcrumbPath = useMemo(
    () => (activeFolderId != null ? buildFolderPath(foldersById, activeFolderId) : []),
    [foldersById, activeFolderId]
  );

  const isSelected = useCallback(
    (row: DriveRow): boolean => {
      const fileId = resolveFileId(row);
      return fileId != null && selected.some(picked => resolveFileId(picked) === fileId);
    },
    [selected]
  );

  /** Тоггл/замена выбора файла или ярлыка (папки игнорируются). */
  const toggleSelect = useCallback(
    (row: DriveRow) => {
      const fileId = resolveFileId(row);
      if (fileId == null) return; // папка
      if (!multiple) {
        setSelected([row]);
        return;
      }
      setSelected(prev =>
        prev.some(picked => resolveFileId(picked) === fileId)
          ? prev.filter(picked => resolveFileId(picked) !== fileId)
          : [...prev, row]
      );
    },
    [multiple]
  );

  const handleRowClick = useCallback(
    (_row: Row<UploadedFile>, rowData: UploadedFile) => {
      const entry: DriveRow = rowData;
      if (isFolderRow(entry)) {
        setActiveFolderId(entry.id); // переход внутрь папки
        return;
      }
      toggleSelect(entry);
    },
    [toggleSelect]
  );

  const handleConfirm = useCallback(
    async (rows: DriveRow[]) => {
      const ids = [...new Set(rows.map(resolveFileId).filter((id): id is number => id != null))];
      if (ids.length === 0) return;
      try {
        setSubmitting(true);
        // Перечитываем чистые UploadedFile по id (ярлык → оригинал уже разрешён)
        const files = await fileRepo.whereIn('id', ids).fetch();
        onSelect(files ?? []);
      } catch (err) {
        logger.error('[DriveFilePickerModal] Ошибка получения файлов:', err);
        setError(err instanceof Error ? err.message : 'Не удалось получить файлы');
      } finally {
        setSubmitting(false);
      }
    },
    [fileRepo, onSelect]
  );

  const handleRowDoubleClick = useCallback(
    (_row: Row<UploadedFile>, rowData: UploadedFile) => {
      const entry: DriveRow = rowData;
      if (isFolderRow(entry)) {
        setActiveFolderId(entry.id);
        return;
      }
      // Двойной клик по файлу в single-режиме — мгновенное подтверждение
      if (!multiple) void handleConfirm([entry]);
    },
    [multiple, handleConfirm]
  );

  const getRowProps = useCallback(
    (row: Row<UploadedFile>): React.HTMLAttributes<HTMLDivElement> => ({
      style: {
        backgroundColor: isSelected(row.original) ? 'var(--mantine-color-blue-0)' : undefined,
        cursor: 'pointer',
      },
    }),
    [isSelected]
  );

  // Пикер read-only: путь не нужен (виден в крошках), меню действий не даём
  const columns = useMemo(() => buildDriveColumns({ foldersById }), [foldersById]);

  const repository = useMemo(
    () =>
      activeFolderId != null
        ? driveRepo.filterByFolder(activeFolderId).withCreatedBy()
        : driveRepo.limit(0),
    // Прокси пересоздаётся на каждый рендер — зависим только от выбранной папки.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeFolderId]
  );

  const hint =
    selected.length > 0
      ? `Выбрано: ${selected.length}`
      : multiple
        ? 'Выберите один или несколько файлов'
        : 'Кликните файл или дважды щёлкните для быстрого выбора';

  if (loading) {
    return (
      <Stack align='center' gap='md' py='xl'>
        <Loader size='md' />
        <Text size='sm' c='dimmed'>
          Загрузка Диска...
        </Text>
      </Stack>
    );
  }

  return (
    <Stack gap='md'>
      {(error ?? loadError) && (
        <Alert color='red' title='Ошибка'>
          {error ?? loadError}
        </Alert>
      )}

      <div style={{ height: 460, display: 'flex', minHeight: 0 }}>
        <DriveLayout
          storageKey='drive-picker-split'
          defaultLeftPercent={26}
          minPercent={14}
          // Дерево read-only: пикер только выбирает файлы, папками не управляет
          tree={
            <FolderTree
              folders={folders}
              activeFolderId={activeFolderId}
              onSelect={setActiveFolderId}
              onRefresh={() => void loadFolders()}
              hideActions
            />
          }
          content={
            <>
              <DriveBreadcrumbs
                path={breadcrumbPath}
                folders={folders}
                onNavigate={setActiveFolderId}
              />
              {activeFolderId != null ? (
                <div style={{ flex: 1, minHeight: 0 }}>
                  <RepositoryTable<UploadedFile>
                    key={activeFolderId}
                    id='drive-picker-files'
                    repository={repository}
                    columns={columns}
                    strictColumns
                    withColumnBorders='header'
                    tableBodyHeight={380}
                    getRowId={row => driveRowId(row)}
                    // Семантика пикера: клик ВЫДЕЛЯЕТ файл, а не открывает его
                    onRowClick={handleRowClick}
                    onRowDoubleClick={handleRowDoubleClick}
                    getRowProps={getRowProps}
                  />
                </div>
              ) : (
                <Text size='sm' c='dimmed' ta='center' py='xl'>
                  Корень Диска не найден
                </Text>
              )}
            </>
          }
        />
      </div>

      <Box
        style={{
          borderTop: '1px solid var(--mantine-color-gray-3)',
          paddingTop: 'var(--mantine-spacing-xs)',
        }}
      >
        <Group justify='space-between'>
          <Text size='sm' c='dimmed'>
            {hint}
          </Text>
          <Group gap='xs'>
            <Button variant='default' onClick={onClose} disabled={submitting}>
              Отмена
            </Button>
            <Button
              leftSection={<IconCheck size={14} />}
              onClick={() => void handleConfirm(selected)}
              disabled={selected.length === 0}
              loading={submitting}
            >
              Выбрать
            </Button>
          </Group>
        </Group>
      </Box>
    </Stack>
  );
}
